import React from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { InfoIconCircle } from '@/assets/icons/info-circle';
import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { EntryConfirmationRow } from '@/components/checkout/EntryConfirmation';
import { ds } from '@/components/lib/design.system';
import { useComboOdds } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { usePredictionTakerFeeTheta } from '@/feature/betslip-sbk/hooks/use-prediction-taker-fee-theta';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { totalBetrBucksStakeSelector, totalStakeSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import {
    getConfirmedPositionSummary,
    getEstimatedPositionSummary,
} from '@/feature/betslip-sbk/utils/prediction-position';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { useShowPositionSummarySheet } from '@/hooks/use-show-position-summary-sheet';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { formatOdds } from '@/utils/format-odds';

import { BetSuccessData } from './use-bet-success-data';

type BetSuccessInfo = {
    title: string;
    rows: EntryConfirmationRow[];
};

export const useBetSuccessInfo = ({ entryType, pickCount, payoutAmount, betIds }: BetSuccessData): BetSuccessInfo => {
    const odds = useComboOdds();
    const oddsFormat = useOddsFormat();
    const totalStake = useSbkBetSlipStore(totalStakeSelector);
    const totalBetrBucksStake = useSbkBetSlipStore(totalBetrBucksStakeSelector);
    const isBetrCash = useIsCashCurrency();
    const isPredictions = useIsPredictionsProduct();
    const takerFeeTheta = usePredictionTakerFeeTheta();
    const { formatCurrency } = useFormatCurrency();
    const showPositionSummarySheet = useShowPositionSummarySheet();
    const { t } = useTranslation('betslip_sbk');

    // Predictions render from the confirmed position fill, delivered by the bet_group_update
    // message or the /auth/bets polling fallback, whichever landed. When neither produced a
    // parseable fill, fall back to the bet slip state captured at submission.
    const confirmedPosition = useSbkBetSlipStore(
        state => Object.values(state.submittedState.submittedBets).find(bet => bet.position)?.position
    );
    const submittedStake = useSbkBetSlipStore(state => state.submittedState.singlesBets[0]?.stake ?? 0);
    const submittedOdds = useSbkBetSlipStore(state => {
        const betId = state.submittedState.singlesBets[0]?.id;
        return betId ? state.submittedState.options[betId]?.odds ?? 0 : 0;
    });

    const isParlay = entryType === 'parlay' || entryType === 'sgp_plus' || entryType === 'sgp';
    const formattedAmount = formatCurrency(payoutAmount);
    const formattedStake = formatCurrency(totalStake);
    const formattedOdds = formatOdds(oddsFormat, odds);

    // The entry is bonus-funded only when the whole stake came from Betr Bucks; a mixed
    // cash + bonus total has no single icon, so we keep it green unless it's fully bonus.
    const isBonusEntry = isBetrCash && totalStake > 0 && totalBetrBucksStake === totalStake;
    const entryIcon = isBonusEntry ? <WalletBetrBucksIcon size={16} /> : <SocialCurrencyActiveIcon size={16} />;

    const parlayInfo: BetSuccessInfo = {
        title: 'Parlay placed!',
        rows: [
            { label: 'Picks', value: pickCount.toString() },
            { label: 'Odds', value: formattedOdds },
            { label: 'Entry amount', value: formattedStake, valuePrefix: entryIcon },
            {
                label: 'Payout',
                value: formattedAmount,
                emphasized: true,
                valuePrefix: <SocialCurrencyActiveIcon size={24} />,
            },
        ],
    };

    const singleInfo: BetSuccessInfo = {
        title: 'Single pick placed!',
        rows: [
            { label: 'Odds', value: formattedOdds },
            { label: 'Entry amount', value: formattedStake, valuePrefix: entryIcon },
            {
                label: 'Payout',
                value: formattedAmount,
                emphasized: true,
                valuePrefix: <SocialCurrencyActiveIcon size={24} />,
            },
        ],
    };

    const multipleInfo: BetSuccessInfo = {
        title: `${betIds.length} entries placed!`,
        rows: [
            { label: 'Entries', value: betIds.length.toString() },
            { label: 'Total entry amount', value: formattedStake, valuePrefix: entryIcon },
            {
                label: 'Total payout',
                value: formattedAmount,
                emphasized: true,
                valuePrefix: <SocialCurrencyActiveIcon size={24} />,
            },
        ],
    };

    const confirmedSummary = confirmedPosition ? getConfirmedPositionSummary(confirmedPosition) : null;
    const positionSummary = confirmedSummary
        ? { ...confirmedSummary, variant: 'actual' as const }
        : {
              odds: submittedOdds,
              ...getEstimatedPositionSummary(submittedStake, submittedOdds, takerFeeTheta),
              variant: 'estimated' as const,
          };
    const predictionInfo: BetSuccessInfo = {
        title: 'Position submitted!',
        rows: [
            { label: 'Price', value: formatOdds(oddsFormat, positionSummary.odds) || '-' },
            { label: 'Cost', value: formatCurrency(positionSummary.cost) },
            {
                label: 'Payout',
                value: formatCurrency(positionSummary.payout),
                emphasized: true,
                labelIcon: (
                    <TouchableOpacity
                        onPress={() => showPositionSummarySheet(positionSummary)}
                        hitSlop={8}
                        accessibilityRole="button"
                        accessibilityLabel={t('position_summary')}
                    >
                        <InfoIconCircle width={16} height={16} color={ds.colorValue.icon.primary} />
                    </TouchableOpacity>
                ),
            },
        ],
    };

    if (isPredictions) {
        return predictionInfo;
    }

    if (isParlay) {
        return parlayInfo;
    }

    if (entryType === 'multiple') {
        return multipleInfo;
    }

    return singleInfo;
};
